import {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import {SerializedError} from "@reduxjs/toolkit";
import {toast} from "react-toastify";
import React, {useCallback, useState} from "react";
import {isErrorWithMessage, isFetchBaseQueryError} from "@utilities/helpers";
import {toastAsError} from "@utilities/toastAsError";
import {IconPlus} from "@tabler/icons-react";

type EditorState = 'idle' | 'create' | 'update' | 'read' | 'delete';
export type MutationError = FetchBaseQueryError | SerializedError | undefined;

type TriggerFn = (p: any) => { unwrap: () => Promise<any>; }
type GetTriggerFn = (URI: string, useCache: boolean) => Promise<any>;

/*interface Triggers<I, U, D> {
    insert?: I;
    update?: U;
    delete?: D;
}*/

interface Triggers {
    get: GetTriggerFn | undefined;
    insert: TriggerFn | undefined;
    update: TriggerFn | undefined;
    delete: TriggerFn | undefined;
}

//export function useEditor<T, I = undefined, U = undefined, D = undefined>(triggers: Triggers<I, U, D>) {
export function useEditor<T extends {
    URI: string | null
}, I = undefined, U = undefined, D = undefined>(insert?: TriggerFn, get?: GetTriggerFn, update?: TriggerFn, del?: TriggerFn) {
    const triggers: Triggers = {get, insert, update: update, delete: del};
    const [editorState, setEditorState] = useState<EditorState>('idle');
    const [resource, setResource] = useState<T | null>(null);
    const [URI, setURI] = useState<string | null>(null);

    const show = (r: T, URI: string) => {
        setResource(r);
        setURI(URI);
        setEditorState('read');
    }
    const edit = (r: Partial<T>, URI: string) => {
        setResource({...r} as T);
        setURI(URI);
        setEditorState('update');
    }
    const create = (r: T) => {
        setResource(r);
        setURI(null);
        setEditorState('create');
    }
    const cancel = useCallback((): void => {
        setResource(null);
        setURI(null);
        setEditorState('idle');
    }, []);

    const inserting = resource !== null && editorState === 'create';
    const updating = resource !== null && editorState === 'update';

    function loadResource(URI: string | null) {
        if (URI)
            if (triggers && triggers.get) {
                const useCache = false;
                triggers.get(URI, useCache).then((res) => {
                    /*
                    {
            "status": "fulfilled",
            "endpointName": "getElemento",
            "requestId": "Wyla0ciqYOof6SY9Z4ip1",
            "originalArgs": "http://localhost:8080/FPNet2021/sistema/elementi-RML/*****1",
            "startedTimeStamp": 1668074384999,
            "data": {
                "codElemento": "*****1",
                "descrizione": "*****1",
                "elementoDiscrezionale": false,
                "insertTrace": {
                    "userName": "SYSTEM",
                    "timeStamp": "2018-01-01T10:00:00"
                }
            },
            "fulfilledTimeStamp": 1668074385037,
            "isUninitialized": false,
            "isLoading": false,
            "isSuccess": true,
            "isError": false,
            "error":{
                "data":{messageCode: -1, message: 'fottiti!'},
                "status":500
                }
                    * */
                    if (res && res.isSuccess) {
                        edit(res.data, URI)
                    } else if (res.isError) {
                        if ('data' in res.error) {
                            // @ts-ignore
                            const message = res.error.data.message;
                            toastAsError(message);
                        } else
                            toastAsError("Errore generico!");
                    }
                });
            } else
                toastAsError("URI NON specificato!");
    }

    /**
     * Metodo per il salvataggio della risorsa, sia per inserimento, sia per aggiornamento
     * @param resource la risorsa da salvare
     * @param auxiliaryValues eventuali dati aggiuntivi o di default
     */
    function saveResource(resource: Partial<T>, auxiliaryValues?: T, auxiliaryURI?: string) {
        const auxValues = auxiliaryValues ? auxiliaryValues : {};
        const localURI = auxiliaryURI || URI;
        console.log(resource);
        if (triggers) {
            if (updating && triggers.update)
                triggers.update({...auxValues, ...resource, URI: localURI!})
                    .unwrap()
                    .then((data) => {
                        edit({...resource, ...data as T}, data.URI);
                        toast('Elemento aggiornato!', {type: 'success'})
                    })
                    .catch((error: MutationError) => {
                        // @ts-ignore
                        const message = error.data.message;
                        toastAsError(message);
                    })
            else if (inserting && triggers.insert)
                triggers.insert({...auxValues, ...resource, URI: localURI!})
                    .unwrap()
                    .then((data) => {
                        edit({...resource, ...data as T}, data.URI);
                        toast('Elemento inserito!', {type: 'success'})
                    })
                    .catch((err) => {
                        if (isFetchBaseQueryError(err)) {
                            // you can access all properties of `FetchBaseQueryError` here
                            //const errMsg = 'error' in err ? err.error : JSON.stringify(err.data)
                            // @ts-ignore
                            const message = err.data.message;
                            toastAsError(message);
                        } else if (isErrorWithMessage(err)) {
                            // you can access a string 'message' property here
                        }
                    })
        }
    }

    function deleteResource(URI: string) {
        if (URI && triggers && triggers.delete) {
            triggers.delete(URI)
                .unwrap()
                .then(() => {
                    if (resource && URI === resource.URI)
                        cancel();
                    toast('Elemento cancellato!', {type: 'success'})
                })
                .catch((error: MutationError) => {
                    // @ts-ignore
                    const message = error.data.message;
                    toastAsError(message);
                })
        }
    }

    return {URI, resource, editorState, show, edit, create, cancel, inserting, updating, loadResource, saveResource, deleteResource}
}

interface CreateButtonProps<T> {
    def?: T,
    label: string,
    create: any
}

export function CreateButton<T>(props: CreateButtonProps<T>) {
    const def = props.def ? props.def : {} as T;
    return (
        <button
            type="button"
            onClick={() => props.create(def)}
            className="inline-flex items-center rounded-md border border-transparent bg-blue-600 p-1.5 sm:p-2 md:py-2 md:px-4 text-sm font-medium leading-4 text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
            <IconPlus className="h-5 w-4 text-white" aria-hidden="true"/>{props.label}
        </button>
    )
}

export function coalesce(val: any, def: any): any {
    //return val;
    if ((val !== null) && (val !== undefined)) {
        return val;
    } else {
        return def;
    }
}